import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';

import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

const DASHBOARD_API = 'https://system.sales-management.cloud/api/dashboard.php';
const NOTICES_API = 'https://system.sales-management.cloud/api/notices.php';

function Home() {
  const [dashboardData, setDashboardData] = useState(null);
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  // ダッシュボードデータ取得
  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      setError('');
      try {
        const token = Cookies.get('auth_token');
        if (!token) {
          setError('認証情報が見つかりません');
          return;
        }

        const dashboardUrl = `${DASHBOARD_API}?year=${selectedYear}&month=${selectedMonth}`;
        const dashboardResponse = await fetch(dashboardUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const dashboardResult = await dashboardResponse.json();

        if (dashboardResult.status === 'success') {
          setDashboardData(dashboardResult.data);
        } else {
          setError(dashboardResult.message || 'データの取得に失敗しました');
          return;
        }

        const noticesResponse = await fetch(`${NOTICES_API}?limit=5`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const noticesResult = await noticesResponse.json();
        if (noticesResult.status === 'success') {
          setNotices(noticesResult.data?.notices || []);
        } else {
          setNotices([]);
        }
      } catch (err) {
        console.error('Fetch error:', err);
        setError('通信エラーが発生しました');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [selectedYear, selectedMonth]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY'
    }).format(amount || 0);
  };

  const formatPercent = (rate) => {
    return `${(rate || 0).toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!dashboardData) return null;

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      {/* ヘッダー */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">ダッシュボード</h1>
          <p className="text-sm text-gray-600 mt-1">
            {dashboardData.user?.name}さん、こんにちは
          </p>
        </div>
        
        {/* 期間選択 */}
        <div className="flex gap-2">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {[2024, 2025, 2026].map(year => (
              <option key={year} value={year}>{year}年</option>
            ))}
          </select>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {[...Array(12)].map((_, i) => (
              <option key={i + 1} value={i + 1}>{i + 1}月</option>
            ))}
          </select>
        </div>
      </div>

      {/* 今日の状況 */}
      {dashboardData.today && (
        <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">今日の状況</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <AssignmentIcon className="text-blue-600" />
              <div>
                <p className="text-sm text-gray-600">日報</p>
                <p className="text-lg font-semibold">
                  {dashboardData.today.has_daily_report ? (
                    <span className="text-green-600">提出済み</span>
                  ) : (
                    <span className="text-red-600">未提出</span>
                  )}
                </p>
              </div>
            </div>
            {dashboardData.today.attendance && (
              <div className="flex items-center gap-3">
                <CheckCircleIcon className="text-green-600" />
                <div>
                  <p className="text-sm text-gray-600">出勤状況</p>
                  <p className="text-lg font-semibold">
                    {dashboardData.today.attendance.clock_in && !dashboardData.today.attendance.clock_out
                      ? '勤務中'
                      : dashboardData.today.attendance.clock_out
                      ? '退勤済み'
                      : '未出勤'}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-sm font-medium text-gray-700 mb-3">お知らせ</p>
            {notices.length === 0 ? (
              <p className="text-sm text-gray-500">現在お知らせはありません</p>
            ) : (
              <div className="space-y-2">
                {notices.map((notice) => (
                  <div key={notice.id} className="bg-gray-50 rounded-md px-3 py-2">
                    <p className="text-sm font-semibold text-gray-900">{notice.title}</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{notice.message}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      有効期限: {notice.expires_at ? new Date(notice.expires_at).toLocaleString('ja-JP') : '無期限'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(notice.created_at).toLocaleString('ja-JP')}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 個人実績 */}
      {dashboardData.personal && (
        <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <PersonIcon className="text-blue-600" />
            <h2 className="text-lg font-bold text-gray-900">個人実績</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <StatCard
              label="総売上"
              value={formatCurrency(dashboardData.personal.sales?.total)}
              icon={<TrendingUpIcon />}
              color="blue"
            />
            <StatCard
              label="完了売上"
              value={formatCurrency(dashboardData.personal.sales?.completed)}
              icon={<CheckCircleIcon />}
              color="green"
            />
            <StatCard
              label="保留中"
              value={formatCurrency(dashboardData.personal.sales?.pending)}
              icon={<HourglassEmptyIcon />}
              color="yellow"
            />
          </div>

          {dashboardData.personal.target && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">目標達成率</span>
                <span className="text-lg font-bold text-blue-600">
                  {formatPercent(dashboardData.personal.target.achievement_rate)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-blue-600 h-3 rounded-full transition-all"
                  style={{ width: `${Math.min(dashboardData.personal.target.achievement_rate, 100)}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-600">
                <span>目標: {formatCurrency(dashboardData.personal.target.amount)}</span>
                <span>残り: {formatCurrency(dashboardData.personal.target.remaining)}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 会社全体 */}
      {dashboardData.company && (
        <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <BusinessIcon className="text-purple-600" />
            <h2 className="text-lg font-bold text-gray-900">会社全体</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <StatCard
              label="総売上"
              value={formatCurrency(dashboardData.company.sales?.total)}
              icon={<TrendingUpIcon />}
              color="purple"
            />
            <StatCard
              label="完了売上"
              value={formatCurrency(dashboardData.company.sales?.completed)}
              icon={<CheckCircleIcon />}
              color="green"
            />
            <StatCard
              label="保留中"
              value={formatCurrency(dashboardData.company.sales?.pending)}
              icon={<HourglassEmptyIcon />}
              color="yellow"
            />
          </div>

          {dashboardData.company.target && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">売上目標達成率</span>
                <span className="text-lg font-bold text-purple-600">
                  {formatPercent(dashboardData.company.target.achievement_rate)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-purple-600 h-3 rounded-full transition-all"
                  style={{ width: `${Math.min(dashboardData.company.target.achievement_rate, 100)}%` }}
                />
              </div>
            </div>
          )}

          {dashboardData.company.appointments && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">アポ目標達成率</span>
                <span className="text-lg font-bold text-purple-600">
                  {formatPercent(dashboardData.company.appointments.achievement_rate)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-purple-600 h-3 rounded-full transition-all"
                  style={{ width: `${Math.min(dashboardData.company.appointments.achievement_rate, 100)}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-600">
                <span>実績: {dashboardData.company.appointments.actual}件</span>
                <span>目標: {dashboardData.company.appointments.target}件</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ランキング */}
      {dashboardData.rankings && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* 売上ランキング */}
          {dashboardData.rankings.sales && dashboardData.rankings.sales.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">売上ランキング</h2>
              <div className="space-y-3">
                {dashboardData.rankings.sales.map((item) => (
                  <RankingItem
                    key={item.user_id}
                    rank={item.rank}
                    name={item.user_name}
                    value={formatCurrency(item.total_amount)}
                    subValue={`完了: ${formatCurrency(item.completed_amount)}`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* アポランキング */}
          {dashboardData.rankings.appointments && dashboardData.rankings.appointments.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">アポランキング</h2>
              <div className="space-y-3">
                {dashboardData.rankings.appointments.map((item) => (
                  <RankingItem
                    key={item.user_id}
                    rank={item.rank}
                    name={item.user_name}
                    value={`${item.total_appointments}件`}
                    subValue={`日報: ${item.reports_count}件`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// 統計カードコンポーネント
const StatCard = ({ label, value, icon, color }) => {
  const colorClasses = {
    blue: 'text-blue-600 bg-blue-50',
    green: 'text-green-600 bg-green-50',
    yellow: 'text-yellow-600 bg-yellow-50',
    purple: 'text-purple-600 bg-purple-50'
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <p className="text-xs text-gray-600">{label}</p>
          <p className="text-lg font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
};

// ランキングアイテムコンポーネント
const RankingItem = ({ rank, name, value, subValue }) => {
  const getRankColor = (rank) => {
    if (rank === 1) return 'bg-yellow-500 text-white';
    if (rank === 2) return 'bg-gray-400 text-white';
    if (rank === 3) return 'bg-orange-600 text-white';
    return 'bg-gray-200 text-gray-700';
  };

  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${getRankColor(rank)}`}>
        {rank}
      </div>
      <div className="flex-1">
        <p className="font-semibold text-gray-900">{name}</p>
        <p className="text-xs text-gray-600">{subValue}</p>
      </div>
      <div className="text-right">
        <p className="font-bold text-gray-900">{value}</p>
      </div>
    </div>
  );
};

export default Home;
